// simple utils
export function getQueryParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

export function getCookie(name) {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
}

// Check if cookies are enabled in the browser
export function areCookiesEnabled() {
  try {
    // Try to set a test cookie
    document.cookie = "test_cookie=1; path=/";
    const enabled = document.cookie.indexOf("test_cookie=1") !== -1;
    
    // Clean up test cookie
    if (enabled) {
      document.cookie = "test_cookie=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
    
    return enabled;
  } catch (error) {
    return false;
  }
}

// Get Facebook tracking data from either cookies or localStorage fallback
export function getFacebookTrackingData() {
  // Try cookie first
  const fbcCookie = getCookie("_fbc");
  if (fbcCookie) {
    return {
      fbc: fbcCookie,
      source: "cookie"
    };
  }

  // Fallback to localStorage
  const fbcFallback = localStorage.getItem("_fbc_fallback");
  if (fbcFallback) {
    return {
      fbc: fbcFallback,
      source: "localStorage"
    };
  }

  return null;
}

// Get Facebook browser ID with fallback support
export function getFacebookBrowserId() {
  // Try cookie first
  const fbpCookie = getCookie("_fbp");
  if (fbpCookie) {
    return {
      fbp: fbpCookie,
      source: "cookie"
    };
  }

  // Fallback to localStorage
  const fbpFallback = localStorage.getItem("_fbp_fallback");
  if (fbpFallback) {
    return {
      fbp: fbpFallback,
      source: "localStorage"
    };
  }

  return null;
}

export function setCookie(name, value, days = 365) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}`;
    
    // Verify cookie was actually set
    const testValue = getCookie(name);
    if (testValue !== value) {
      console.warn(`Cookie '${name}' could not be set. Browser may have cookies disabled.`);
      return false;
    }
    return true;
  } catch (error) {
    console.warn(`Failed to set cookie '${name}':`, error.message);
    return false;
  }
}

/**
 * Reads ?fbclid=… from the current URL and stores it as the _fbc attribution
 * cookie (with localStorage fallback when cookies are disabled).
 *
 * Call once when the landing page mounts — metaPageTracking.trackLandingPageVisit()
 * already does this for you.
 *
 * @returns {string|null} The _fbc value if fbclid was present, otherwise null.
 */
export function captureAndStoreFbclid() {
  const fbclid = getQueryParam('fbclid');
  if (!fbclid) return null;

  localStorage.setItem('fbclid', fbclid);

  const fbc    = `fb.1.${Math.floor(Date.now() / 1000)}.${fbclid}`;
  const stored = setCookie('_fbc', fbc);
  if (!stored) localStorage.setItem('_fbc_fallback', fbc);
  return fbc;
}

// ─── Legacy pixel / iOS functions kept for backward compatibility ─────────────
// New code should NOT call functions below. Use metaPageTracking.js instead.
// Initialize Facebook Pixel with iPhone/iOS compatibility - safe for existing pixels
export function initializeFacebookPixel(pixelId) {
  if (typeof window === 'undefined') return false;
  
  try {
    // Check if pixel already exists (from HTML or previous initialization)
    if (window.fbq && typeof window.fbq === 'function') {
      console.log('Facebook Pixel already initialized - enhancing for iPhone compatibility');
      
      // Just enhance existing pixel for iPhone without reinitializing
      enhanceExistingPixelForIOS(pixelId);
      return true;
    }
    
    // Only initialize if pixel doesn't exist
    const isIOSDevice = isIOS();
    console.log('Initializing Facebook Pixel for', isIOSDevice ? 'iOS' : 'other platforms');
    
    // Create fbq function with iPhone compatibility
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return; // Safety check
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      
      // For iOS - use different loading strategy
      if (isIOSDevice) {
        loadPixelScriptForIOS(pixelId);
      } else {
        // Standard loading for other devices
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    
    // Initialize pixel
    window.fbq('init', pixelId, {
      external_id: generateExternalId(),
      disable_auto_config: isIOSDevice,
      manual_mode: isIOSDevice
    });
    
    // Track PageView with iOS-specific data
    trackPageViewForIOS();
    
    return true;
  } catch (error) {
    console.error('Failed to initialize Facebook Pixel:', error);
    return false;
  }
}

// Enhance existing pixel for iPhone compatibility without breaking it - ULTRA SAFE
function enhanceExistingPixelForIOS(pixelId) {
  // Wrap everything to ensure existing pixel never breaks
  try {
    if (!isIOS()) {
      console.log('Not iOS device - skipping enhancement');
      return; // Only enhance for iOS
    }
    
    console.log('📱 Enhancing existing Facebook Pixel for iOS compatibility');
    
    // Add iPhone-specific enhancements to existing pixel
    if (window.fbq && typeof window.fbq === 'function') {
      try {
        // Store original fbq function
        const originalFbq = window.fbq;
        
        // Create enhanced wrapper for iOS - with multiple safety layers
        window.fbq = function() {
          try {
            // ALWAYS call original function first - this is critical
            const result = originalFbq.apply(this, arguments);
            
            // Only add iOS enhancements if original call succeeded
            try {
              // Add iOS-specific tracking if this is a track call
              if (arguments[0] === 'track' && arguments.length >= 2) {
                const eventName = arguments[1];
                const eventData = arguments[2] || {};
                
                // Enhance with iOS-specific data safely
                try {
                  const enhancedData = {
                    ...eventData,
                    external_id: generateExternalId(),
                    client_user_agent: navigator.userAgent || '',
                    event_time: Math.floor(Date.now() / 1000)
                  };
                  
                  // Send iOS-specific custom event as backup - delayed to not interfere
                  setTimeout(() => {
                    try {
                      originalFbq('trackCustom', `iOS${eventName}`, enhancedData);
                    } catch (customError) {
                      console.warn('iOS custom tracking failed (non-critical):', customError);
                      // Non-critical - don't affect anything
                    }
                  }, 100);
                } catch (enhanceError) {
                  console.warn('iOS data enhancement failed (non-critical):', enhanceError);
                  // Non-critical - original tracking still worked
                }
              }
            } catch (iosEnhanceError) {
              console.warn('iOS enhancement wrapper failed (non-critical):', iosEnhanceError);
              // Non-critical - original call already succeeded
            }
            
            return result;
          } catch (wrapperError) {
            console.warn('Enhanced iOS wrapper error, falling back to original:', wrapperError);
            // Critical fallback - call original function directly
            try {
              return originalFbq.apply(this, arguments);
            } catch (originalError) {
              console.error('Even original fbq call failed:', originalError);
              // At this point, there's a deeper issue, but we've tried everything
              return undefined;
            }
          }
        };
        
        // Preserve all original properties safely
        try {
          Object.keys(originalFbq).forEach(key => {
            try {
              window.fbq[key] = originalFbq[key];
            } catch (propError) {
              console.warn(`Failed to preserve fbq property ${key}:`, propError);
              // Non-critical - continue with other properties
            }
          });
        } catch (preserveError) {
          console.warn('Failed to preserve some fbq properties:', preserveError);
          // Non-critical - basic functionality should still work
        }
        
        console.log('✅ iPhone enhancement applied safely to existing Facebook Pixel');
      } catch (enhanceError) {
        console.error('Failed to enhance existing pixel for iOS:', enhanceError);
        // Critical: Make sure original pixel is still accessible
        console.log('Original pixel should still be working normally');
      }
    } else {
      console.warn('No existing Facebook Pixel found to enhance');
    }
  } catch (error) {
    console.error('Critical error in iOS enhancement (pixel should still work):', error);
    // Even if enhancement completely fails, original pixel from HTML should work
  }
}

// iOS-specific script loading with retry mechanism
function loadPixelScriptForIOS(pixelId) {
  let retryCount = 0;
  const maxRetries = 3;
  
  function attemptLoad() {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      
      script.onload = function() {
        console.log('✅ Facebook Pixel loaded successfully on iOS');
        // Verify pixel is working
        setTimeout(() => verifyPixelOnIOS(pixelId), 1000);
      };
      
      script.onerror = function() {
        console.warn('❌ Facebook Pixel failed to load on iOS, attempt:', retryCount + 1);
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(attemptLoad, 2000); // Retry after 2 seconds
        } else {
          console.error('Facebook Pixel failed to load after', maxRetries, 'attempts on iOS');
        }
      };
      
      // For iOS, try multiple insertion methods
      const head = document.head || document.getElementsByTagName('head')[0];
      const firstScript = document.getElementsByTagName('script')[0];
      
      if (head) {
        head.appendChild(script);
      } else if (firstScript) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.body.appendChild(script);
      }
    } catch (error) {
      console.error('Error loading Facebook Pixel script on iOS:', error);
    }
  }
  
  attemptLoad();
}

// Verify pixel is working on iOS
function verifyPixelOnIOS(pixelId) {
  if (window.fbq && typeof window.fbq === 'function') {
    console.log('✅ Facebook Pixel verification successful on iOS');
    
    // Test basic functionality
    try {
      window.fbq('track', 'PageView');
      console.log('✅ PageView tracking test successful on iOS');
    } catch (error) {
      console.error('❌ PageView tracking test failed on iOS:', error);
    }
  } else {
    console.error('❌ Facebook Pixel verification failed on iOS');
  }
}

// iOS-optimized PageView tracking
function trackPageViewForIOS() {
  try {
    if (window.fbq && typeof window.fbq === 'function') {
      const pageData = {
        // Add iOS-specific page data
        content_name: document.title || 'Page',
        source_url: window.location.href,
        user_agent: navigator.userAgent,
        // Add external ID for iOS attribution
        external_id: generateExternalId()
      };
      
      window.fbq('track', 'PageView', pageData);
      
      // For iOS - also send a custom event as backup
      if (isIOS()) {
        window.fbq('trackCustom', 'iOSPageView', pageData);
      }
      
      console.log('📱 iOS PageView tracked with enhanced data');
    }
  } catch (error) {
    console.error('iOS PageView tracking failed:', error);
  }
}

// Enhanced Purchase tracking for iOS - NEVER BREAKS USER FLOW
export function trackFacebookPurchase(orderData) {
  // Wrap everything to ensure user flow never breaks
  try {
    if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
      const purchaseData = {
        value: orderData.value || 0,
        currency: orderData.currency || 'INR',
        content_ids: orderData.content_ids || [],
        content_type: 'product'
      };
      
      try {
        // Add iOS-specific data safely
        const enhancedData = {
          ...purchaseData,
          external_id: generateExternalId(),
          order_id: orderData.order_id || '',
          event_time: Math.floor(Date.now() / 1000)
        };
        
        // Primary purchase tracking - with fallback
        try {
          window.fbq('track', 'Purchase', enhancedData);
        } catch (primaryError) {
          console.warn('Primary Purchase tracking failed, trying basic version:', primaryError);
          // Fallback to basic tracking
          window.fbq('track', 'Purchase', purchaseData);
        }
        
        // iOS backup tracking - optional, never throws
        if (isIOS()) {
          try {
            window.fbq('trackCustom', 'iOSPurchase', enhancedData);
          } catch (iosError) {
            console.warn('iOS Purchase backup failed (non-critical):', iosError);
            // Non-critical - don't affect user flow
          }
        }
        
        console.log('💰 Purchase tracked successfully');
        return { success: true, data: enhancedData };
      } catch (enhancementError) {
        console.warn('Purchase enhancement failed, using basic tracking:', enhancementError);
        // Fallback to basic tracking
        try {
          window.fbq('track', 'Purchase', purchaseData);
          return { success: true, data: purchaseData, fallback: true };
        } catch (basicError) {
          console.warn('Even basic Purchase tracking failed:', basicError);
          return { success: false, reason: 'All Purchase tracking failed' };
        }
      }
    } else {
      console.warn('Facebook Pixel not available for Purchase tracking');
      return { success: false, reason: 'Facebook Pixel not available' };
    }
  } catch (error) {
    // Absolutely silent fail - NEVER break user flow
    console.warn('Purchase tracking completely failed (silent):', error);
    return { success: false, reason: 'Complete tracking failure', silent: true };
  }
}
// Track Facebook Lead event with null safety and iPhone compatibility - NEVER BREAKS USER FLOW
export function trackFacebookLead(product) {
  // Wrap everything in try-catch to ensure user flow never breaks
  try {
    // Check if Facebook Pixel is available and functional
    if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
      const trackingData = {
        content_name: (product && product.name) ? String(product.name) : 'Astrology Service',
        content_category: 'Astrology',
        value: (product && product.price && !isNaN(product.price)) ? Number(product.price) : 0,
        currency: 'INR'
      };
      
      // Add iPhone/iOS specific tracking data safely
      try {
        const enhancedData = {
          ...trackingData,
          // Add external_id for better matching on iOS
          external_id: generateExternalId(),
          // Add client_user_agent for better attribution
          client_user_agent: navigator.userAgent || '',
          // Add timestamp for iOS attribution
          event_time: Math.floor(Date.now() / 1000)
        };
        
        // Primary tracking call - wrapped in try-catch
        try {
          window.fbq('track', 'Lead', enhancedData);
        } catch (primaryError) {
          console.warn('Primary Lead tracking failed, trying basic version:', primaryError);
          // Fallback to basic tracking without enhancements
          window.fbq('track', 'Lead', trackingData);
        }
        
        // For iPhone/iOS - also try trackCustom as fallback (never throw errors)
        if (isIOS()) {
          try {
            window.fbq('trackCustom', 'iOSLead', enhancedData);
          } catch (iosError) {
            console.warn('iOS custom tracking failed (non-critical):', iosError);
            // This is optional - don't affect user flow
          }
        }
        
        return { success: true, data: enhancedData, platform: isIOS() ? 'iOS' : 'other' };
      } catch (enhancementError) {
        console.warn('Enhancement failed, using basic tracking:', enhancementError);
        // Fallback to basic tracking if enhancement fails
        try {
          window.fbq('track', 'Lead', trackingData);
          return { success: true, data: trackingData, fallback: true };
        } catch (basicError) {
          console.warn('Even basic tracking failed:', basicError);
          return { success: false, reason: 'All tracking methods failed' };
        }
      }
    } else {
      console.warn('Facebook Pixel not available for Lead tracking');
      return { success: false, reason: 'Facebook Pixel not available' };
    }
  } catch (error) {
    // Absolutely silent fail - NEVER break user flow
    console.warn('Facebook Lead tracking completely failed (silent):', error);
    return { success: false, reason: 'Complete tracking failure', silent: true };
  }
}

// Helper function to detect iOS devices
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Generate a unique external ID for better iOS tracking - NEVER THROWS ERRORS
function generateExternalId() {
  try {
    // Try to get a consistent ID from localStorage
    let externalId = localStorage.getItem('fb_external_id');
    
    if (!externalId) {
      try {
        // Generate a new ID combining timestamp and random
        externalId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        
        try {
          localStorage.setItem('fb_external_id', externalId);
        } catch (storageError) {
          console.warn('Cannot store external ID in localStorage (non-critical):', storageError);
          // Still return the generated ID even if we can't store it
        }
      } catch (generateError) {
        console.warn('Failed to generate external ID, using fallback:', generateError);
        // Fallback to simple timestamp
        externalId = Date.now().toString();
      }
    }
    
    return externalId;
  } catch (error) {
    console.warn('External ID generation completely failed, using timestamp fallback:', error);
    // Ultimate fallback - just use timestamp
    try {
      return Date.now().toString();
    } catch (timestampError) {
      console.warn('Even timestamp fallback failed, using static ID:', timestampError);
      // Final fallback - static but unique enough ID
      return 'fallback_' + Math.random().toString(36).substr(2, 9);
    }
  }
}

// Comprehensive debug function for iPhone tracking issues
export function debugiPhoneTracking() {
  const debug = {
    // Device Detection
    isIOS: isIOS(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    
    // Browser Capabilities
    cookiesEnabled: areCookiesEnabled(),
    localStorage: (() => {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
      } catch (e) {
        return false;
      }
    })(),
    
    // Facebook Pixel Status
    facebookPixelLoaded: !!(window.fbq),
    facebookPixelFunction: !!(window.fbq && typeof window.fbq === 'function'),
    facebookPixelQueue: window.fbq ? window.fbq.queue : null,
    
    // Network and Script Loading
    scriptsInHead: Array.from(document.head.querySelectorAll('script')).map(s => s.src).filter(src => src.includes('facebook')),
    
    // Tracking Data
    trackingData: getFacebookTrackingData(),
    browserId: getFacebookBrowserId(),
    externalId: localStorage.getItem('fb_external_id'),
    
    // iOS Specific Checks
    safariVersion: navigator.userAgent.match(/Version\/([0-9\._]+).*Safari/)?.[1] || 'Unknown',
    preventCrossSiteTracking: (() => {
      try {
        // Try to detect if cross-site tracking is prevented
        return document.cookie.length === 0 && !areCookiesEnabled();
      } catch (e) {
        return 'Unknown';
      }
    })(),
    
    // Current Page Info
    currentUrl: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    
    // Facebook Pixel Test
    pixelTest: (() => {
      try {
        if (window.fbq && typeof window.fbq === 'function') {
          // Try to call fbq and see if it responds
          const originalQueue = window.fbq.queue ? [...window.fbq.queue] : [];
          window.fbq('track', 'TestEvent', { test: true });
          return {
            canCall: true,
            queueGrows: window.fbq.queue ? window.fbq.queue.length > originalQueue.length : false
          };
        }
        return { canCall: false, reason: 'fbq not available' };
      } catch (error) {
        return { canCall: false, error: error.message };
      }
    })()
  };
  
  console.log('� Comprehensive iPhone/iOS Tracking Debug:', debug);
  
  // Additional iOS-specific warnings
  if (debug.isIOS) {
    console.log('📱 iOS Device Detected - Special Considerations:');
    console.log('   • Intelligent Tracking Prevention may be active');
    console.log('   • App Tracking Transparency may require permission');
    console.log('   • Cross-site tracking prevention may be enabled');
    console.log('   • Consider using Conversions API as backup');
  }
  
  return debug;
}
